import { IN_TRAY } from '../model/areas.js';
import { builtinLayerId } from '../model/layers.js';
import { createBoardData, makeItem } from '../model/board.js';
import { newId } from '../model/ids.js';
import type {
  BoardData,
  Channel,
  Connection,
  FreeformElement,
  Item,
  Mix,
  PowerSource,
} from '../model/schema.js';
import { BUILTIN_CATALOG } from '../catalog/builtin.js';
import { findCatalogEntry, itemFromCatalog } from '../catalog/catalog.js';

/**
 * A realistic five-piece band on a 10 × 6 m stage, used for demos, e2e tests and screenshots.
 * Mixes formal data (ports, cables, sub-core, power) with informal data (talkback channel with
 * free-text patch), and contains exactly one deliberate validation error (a 120 V-only keyboard
 * on a 230 V circuit) so validation can be demonstrated.
 *
 * Coordinates are stage-relative metres: x from stage right (audience left) to stage left,
 * y from upstage (0) to downstage (depth). Rotation 0 faces the audience.
 */
export function createDemoBand(name = 'Demo Band – Club Tour'): BoardData {
  const data = createBoardData(name, { width: 10, depth: 6 });
  const items: Item[] = [];
  const add = (item: Item): Item => {
    items.push(item);
    return item;
  };
  const cat = (ref: string, overrides: Partial<Item>): Item => {
    // Non-null: every ref below exists in the built-in catalog (asserted by the fixture test).
    const entry = findCatalogEntry([BUILTIN_CATALOG], ref)!;
    return add(itemFromCatalog(entry, overrides));
  };
  const person = (label: string, x: number, y: number): Item =>
    add(makeItem('musician', { label, x, y }));

  // --- stage & people -------------------------------------------------------------------------
  cat('generic-riser-2x2', { label: 'Drum riser 40 cm', x: 5, y: 1.4, z: 0.4 });
  person('Drums – Kim', 5, 1.4);
  // The drum mics are shown as one list box next to the kit (decision U2).
  const kit = add(
    makeItem('instrument', {
      label: 'Drum kit (Kick, Snare, HH, 2 Toms, OH)',
      x: 5,
      y: 1.4,
      childDisplay: 'list',
    }),
  );
  person('Bass – Alex', 7.6, 2.6);
  person('Guitar – Sam', 2.4, 2.6);
  person('Keys – Jo', 8.4, 1.2);
  person('Lead vocals – Robin', 5, 4.4);

  // --- backline ---------------------------------------------------------------------------------
  const bassAmp = add(
    makeItem('instrument', {
      label: 'Bass amp (Ampeg SVT)',
      x: 8.4,
      y: 2.2,
      power: { voltages: [230], frequency: 50, watts: 500 },
    }),
  );
  const gtrAmp = add(
    makeItem('instrument', {
      label: 'Guitar amp (Vox AC30)',
      x: 1.6,
      y: 2.2,
      power: { voltages: [230], frequency: 50, watts: 100 },
    }),
  );
  const keys = add(
    makeItem('instrument', {
      label: 'Keys (US Nord, 120 V only)',
      x: 8.4,
      y: 1.6,
      rotation: 270,
      power: { voltages: [120], frequency: 60, watts: 60 },
    }),
  );

  // --- microphones / DIs ---------------------------------------------------------------------
  const kickIn = cat('shure-beta91a', { label: 'Kick in', x: 5, y: 0.9, group: 'Drums' });
  const kickOut = cat('shure-beta52a', {
    label: 'Kick out',
    x: 5,
    y: 1.95,
    rotation: 180,
    group: 'Drums',
  });
  const snare = cat('shure-sm57', {
    label: 'Snare top',
    x: 4.5,
    y: 1.7,
    rotation: 135,
    group: 'Drums',
  });
  const hihat = cat('neumann-km184', {
    label: 'Hi-hat',
    x: 4.1,
    y: 1.5,
    rotation: 90,
    group: 'Drums',
  });
  const tom1 = cat('sennheiser-e604', {
    label: 'Tom 1',
    x: 4.8,
    y: 1.0,
    rotation: 180,
    group: 'Drums',
  });
  const tom2 = cat('sennheiser-e604', {
    label: 'Floor tom',
    x: 5.7,
    y: 1.6,
    rotation: 225,
    group: 'Drums',
  });
  const ohL = cat('neumann-km184', {
    label: 'OH L',
    x: 4.3,
    y: 0.7,
    rotation: 180,
    z: 1.8,
    group: 'Drums',
  });
  const ohR = cat('neumann-km184', {
    label: 'OH R',
    x: 5.7,
    y: 0.7,
    rotation: 180,
    z: 1.8,
    group: 'Drums',
  });
  const bassDi = cat('radial-j48', { label: 'Bass DI', x: 8.0, y: 2.3, group: 'Bass' });
  const gtrMic = cat('sennheiser-e906', {
    label: 'Guitar amp',
    x: 1.6,
    y: 2.6,
    rotation: 180,
    group: 'Guitar',
  });
  const keysDi = cat('radial-prod2', { label: 'Keys DI (stereo)', x: 7.9, y: 1.4, group: 'Keys' });
  const leadVox = cat('shure-ulxd2-sm58', {
    label: 'Lead vocal (wireless)',
    x: 5,
    y: 4.6,
    group: 'Vocals',
  });
  const leadRx = cat('shure-ulxd4', { label: 'ULXD4 receiver', ...IN_TRAY, x: 0, y: 0 });
  const bv1 = cat('shure-sm58', { label: 'BV guitar', x: 2.4, y: 3.0, group: 'Vocals' });
  const bv2 = cat('shure-sm58', { label: 'BV bass', x: 7.6, y: 3.0, group: 'Vocals' });
  // Stands (decision U1): formal specs from the catalog, mics reference them via mountedOn.
  const mount = (mic: Item, ref: string, label: string, x: number, y: number): void => {
    mic.mountedOn = cat(ref, { label, x, y }).id;
  };
  mount(bv1, 'generic-stand-boom-tall', 'Boom BV guitar', 2.4, 3.2);
  mount(bv2, 'generic-stand-boom-tall', 'Boom BV bass', 7.6, 3.2);
  mount(gtrMic, 'generic-stand-boom-short', 'Short boom guitar amp', 1.6, 2.8);
  mount(kickOut, 'generic-stand-boom-short', 'Short boom kick', 5, 2.1);
  mount(ohL, 'generic-stand-overhead', 'OH stand L', 4.2, 0.6);
  mount(ohR, 'generic-stand-overhead', 'OH stand R', 5.8, 0.6);
  for (const [mic, label] of [
    [snare, 'Clamp snare'],
    [tom1, 'Clamp tom 1'],
    [tom2, 'Clamp floor tom'],
    [hihat, 'Clamp hi-hat'],
  ] as const) {
    mount(mic, 'generic-drum-clamp', label, mic.x, mic.y);
  }

  // --- stage box, sub-core, console ----------------------------------------------------------
  const subcore = cat('generic-subcore-8', { label: 'Drum sub-core', x: 6.1, y: 0.6 });
  const stagebox = cat('midas-dl32', { label: 'SB1 – DL32', x: 0.8, y: 0.5 });
  const consoleItem = cat('generic-console', { label: 'FOH console', ...IN_TRAY, x: 0, y: 0 });

  // --- monitors ------------------------------------------------------------------------------
  const wedgeVoxL = cat('dnb-m4', { label: 'Vox wedge L', x: 4.4, y: 5.5, rotation: 180 });
  const wedgeVoxR = cat('dnb-m4', { label: 'Vox wedge R', x: 5.6, y: 5.5, rotation: 180 });
  const wedgeGtr = cat('dnb-m4', { label: 'Guitar wedge', x: 2.4, y: 3.8, rotation: 180 });
  const wedgeBass = cat('dnb-m4', { label: 'Bass wedge', x: 7.6, y: 3.8, rotation: 180 });
  const drumFill = cat('dnb-max2', { label: 'Drum fill', x: 6.2, y: 1.9, rotation: 225 });
  const sideL = cat('dnb-y10p', { label: 'Sidefill SR', x: 0.4, y: 3.2, rotation: 90 });
  const sideR = cat('dnb-y10p', { label: 'Sidefill SL', x: 9.6, y: 3.2, rotation: 270 });
  const iem = cat('shure-psm1000', { label: 'Keys IEM', x: 9.3, y: 0.6 });

  // --- power ---------------------------------------------------------------------------------
  const distro = cat('generic-cee16-distro', { label: 'Power SL (CEE16)', x: 9.4, y: 1.8 });
  const source: PowerSource = {
    id: newId(),
    name: 'Stage left CEE16',
    voltage: 230,
    frequency: 50,
    plug: 'cee16',
    maxAmps: 16,
    itemId: distro.id,
  };
  for (const load of [bassAmp, gtrAmp, keys, stagebox, iem]) {
    load.power = { ...load.power, sourceId: source.id };
  }
  stagebox.power = { ...stagebox.power, watts: 60 };
  iem.power = { ...iem.power, watts: 40 };

  // --- cabling (formal signal chain) ---------------------------------------------------------
  const connections: Connection[] = [];
  const cable = (
    fromItem: Item,
    fromPort: string,
    toItem: Item,
    toPort: string,
    extra: Partial<Connection> = {},
  ): void => {
    connections.push({
      id: newId(),
      from: { itemId: fromItem.id, portId: fromPort },
      to: { itemId: toItem.id, portId: toPort },
      ...extra,
    });
  };
  const drumMics = [kickIn, kickOut, snare, hihat, tom1, tom2, ohL, ohR];
  for (const mic of drumMics) mic.parentId = kit.id;
  drumMics.forEach((mic, i) =>
    cable(mic, 'out', subcore, `in${i + 1}`, { cableType: 'XLR', length: 3 }),
  );
  drumMics.forEach((_, i) => cable(subcore, `out${i + 1}`, stagebox, `in${i + 1}`));
  cable(bassDi, 'out', stagebox, 'in9', { cableType: 'XLR', length: 10 });
  cable(gtrMic, 'out', stagebox, 'in10', { cableType: 'XLR', length: 5 });
  cable(keysDi, 'outl', stagebox, 'in11', { cableType: 'XLR', length: 10 });
  cable(keysDi, 'outr', stagebox, 'in12', { cableType: 'XLR', length: 10 });
  cable(leadVox, 'rf', leadRx, 'rf');
  cable(leadRx, 'out', stagebox, 'in13', { cableType: 'XLR', length: 2 });
  cable(bv1, 'out', stagebox, 'in14', { cableType: 'XLR', length: 5 });
  cable(bv2, 'out', stagebox, 'in15', { cableType: 'XLR', length: 10 });
  cable(stagebox, 'aes50a', consoleItem, 'aes50a', {
    cableType: 'Cat6 (AES50)',
    length: 50,
    label: 'Snake to FOH',
  });

  // --- channels ------------------------------------------------------------------------------
  const channels: Channel[] = [];
  const ch = (
    number: number,
    name: string,
    src: Item | null,
    port: string | undefined,
    extra: Partial<Channel> = {},
  ): Channel => {
    const c: Channel = {
      id: newId(),
      number,
      name,
      ...(src ? { sourceItemId: src.id, sourcePortId: port } : {}),
      ...extra,
    };
    channels.push(c);
    return c;
  };
  const cKick = ch(1, 'Kick in', kickIn, 'out', { group: 'Drums', phantom: true });
  ch(2, 'Kick out', kickOut, 'out', { group: 'Drums' });
  const cSnare = ch(3, 'Snare', snare, 'out', { group: 'Drums', insert: 'Comp' });
  ch(4, 'Hi-hat', hihat, 'out', { group: 'Drums', phantom: true });
  ch(5, 'Tom 1', tom1, 'out', { group: 'Drums' });
  ch(6, 'Floor tom', tom2, 'out', { group: 'Drums' });
  ch(7, 'OH L', ohL, 'out', { group: 'Drums', phantom: true, stand: 'Tall boom' });
  ch(8, 'OH R', ohR, 'out', { group: 'Drums', phantom: true, stand: 'Tall boom' });
  const cBass = ch(9, 'Bass DI', bassDi, 'out', { group: 'Bass', phantom: true });
  const cGtr = ch(10, 'Guitar', gtrMic, 'out', { group: 'Guitar' });
  const cKeysL = ch(11, 'Keys L', keysDi, 'outl', { group: 'Keys' });
  const cKeysR = ch(12, 'Keys R', keysDi, 'outr', { group: 'Keys' });
  const cLead = ch(13, 'Lead vocal', leadRx, 'out', { group: 'Vocals', insert: 'Comp' });
  const cBv1 = ch(14, 'BV guitar', bv1, 'out', { group: 'Vocals', stand: 'Tall boom' });
  const cBv2 = ch(15, 'BV bass', bv2, 'out', { group: 'Vocals', stand: 'Tall boom' });
  ch(16, 'Talkback FOH', null, undefined, {
    sourceText: 'SM58 with switch at FOH',
    patch: 'FOH local 1',
    notes: 'Bring own TB mic',
  });

  // --- mixes (one mix can feed several monitors) ---------------------------------------------
  const sends = (...cs: Channel[]) => cs.map((c) => ({ channelId: c.id }));
  const mixes: Mix[] = [
    {
      id: newId(),
      number: 1,
      name: 'Lead vocal',
      kind: 'monitor',
      stereo: false,
      sends: sends(cLead, cKeysL, cGtr),
      outputItemIds: [wedgeVoxL.id, wedgeVoxR.id],
    },
    {
      id: newId(),
      number: 2,
      name: 'Guitar',
      kind: 'monitor',
      stereo: false,
      sends: sends(cGtr, cLead, cBv1, cSnare),
      outputItemIds: [wedgeGtr.id],
    },
    {
      id: newId(),
      number: 3,
      name: 'Bass',
      kind: 'monitor',
      stereo: false,
      sends: sends(cBass, cKick, cLead, cBv2),
      outputItemIds: [wedgeBass.id],
    },
    {
      id: newId(),
      number: 4,
      name: 'Drums',
      kind: 'monitor',
      stereo: false,
      sends: sends(cKick, cBass, cLead),
      outputItemIds: [drumFill.id],
    },
    {
      id: newId(),
      number: 5,
      name: 'Keys IEM',
      kind: 'iem',
      stereo: true,
      sends: sends(cKeysL, cKeysR, cLead, cKick, cSnare),
      outputItemIds: [iem.id],
    },
    {
      id: newId(),
      number: 6,
      name: 'Sidefills',
      kind: 'monitor',
      stereo: true,
      sends: sends(cKick, cSnare, cBass, cGtr, cLead),
      outputItemIds: [sideL.id, sideR.id],
    },
  ];

  // --- free-form discussion area next to the stage -------------------------------------------
  const freeform: FreeformElement[] = [
    {
      id: newId(),
      kind: 'text',
      x: 0,
      y: -1.2,
      w: 6,
      h: 0.8,
      rotation: 0,
      text: 'Demo Band – Club Tour 2027',
    },
    {
      id: newId(),
      kind: 'sticky',
      x: 11,
      y: 0,
      w: 2.4,
      h: 2,
      rotation: 0,
      color: '#ffe58a',
      text: 'Load-in 14:00\nSoundcheck 16:30\nDoors 19:00',
    },
    {
      id: newId(),
      kind: 'sticky',
      x: 11,
      y: 2.4,
      w: 2.4,
      h: 2,
      rotation: 0,
      color: '#ffc4a3',
      text: 'Venue: do you have a 2nd drum riser? Otherwise we bring ours.',
    },
    {
      id: newId(),
      kind: 'sticky',
      x: 5.6,
      y: 3.6,
      w: 1.6,
      h: 1.1,
      rotation: 0,
      color: '#b6dcff',
      text: 'Lead vox moves a lot – wireless!',
      layerId: builtinLayerId('notes'),
    },
  ];

  for (const i of items) data.items[i.id] = i;
  for (const c of connections) data.connections[c.id] = c;
  for (const c of channels) data.channels[c.id] = c;
  for (const m of mixes) data.mixes[m.id] = m;
  for (const f of freeform) data.freeform[f.id] = f;
  data.power[source.id] = source;
  return data;
}
